<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisToggle from 'dashboard/components-next/LaisToggle.vue';
import {
  maritalStatusOptions,
  marriageRegimeOptions,
} from '../composables/salesPartyFormOptions';
import { formatCpf } from '../composables/commissionPaymentValidation';
import SalesPartyMarriageAgreementAlert from './SalesPartyMarriageAgreementAlert.vue';
import SalesPropertyReferenceField from './SalesPropertyReferenceField.vue';

const props = defineProps({
  errors: { type: Object, default: () => ({}) },
  identity: { type: Object, default: () => ({}) },
  referenceLabels: { type: Boolean, default: false },
  showSpouseCpf: { type: Boolean, default: true },
  spouseLookupError: { type: String, default: '' },
  spouseLookupLoading: { type: Boolean, default: false },
});

const emit = defineEmits(['update']);
const { t } = useI18n();
const maritalStatuses = computed(() => maritalStatusOptions(t));
const marriageRegimes = computed(() => marriageRegimeOptions(t));
const isMarried = computed(() => props.identity.marital_status === '1');
const hasStableUnion = computed(() =>
  [true, 'true', '1', 'S'].includes(props.identity.stable_union)
);
const canDeclareStableUnion = computed(
  () => Number(props.identity.marital_status) > 1
);
const needsSpouse = computed(() => isMarried.value || hasStableUnion.value);
const spouseCpf = computed(() => formatCpf(props.identity.spouse_cpf));
const marriageAgreementDismissed = ref(false);
const showMarriageAgreement = computed(
  () =>
    isMarried.value &&
    ['1', '2', '3'].includes(String(props.identity.marital_property_regime)) &&
    !marriageAgreementDismissed.value
);
const maritalStatusLabel = computed(() =>
  props.referenceLabels
    ? t('SALES_PARTIES.PERSONAL.FORM_MARITAL_STATUS')
    : t('SALES_PARTIES.FORM.MARITAL_STATUS')
);
const spouseNameLabel = computed(() =>
  props.referenceLabels
    ? t('SALES_PARTIES.PERSONAL.FORM_SPOUSE_NAME')
    : t('SALES_PARTIES.FORM.SPOUSE_NAME')
);
const isFilled = value => Boolean(String(value || '').trim());
const referenceControlClasses = ({ filled, disabled, error }) => {
  const classes = [
    'h-8 w-full border-0 border-b bg-transparent px-0 text-base outline-none placeholder:text-[#A7B7BE] focus:ring-0',
  ];
  if (error) {
    classes.push('border-[#D64545] text-[#D64545]');
  } else if (filled && !disabled) {
    classes.push('border-[#009F94] font-bold text-[#009F94]');
  } else {
    classes.push('border-[#A7B7BE] text-[#7F8C93]');
  }
  if (disabled) classes.push('cursor-not-allowed text-[#7F8C93]');
  return classes;
};
const update = (field, value) => emit('update', field, value);
const updateSpouseCpf = value => update('spouse_cpf', formatCpf(value));

watch(
  () => props.identity.marital_property_regime,
  () => {
    marriageAgreementDismissed.value = false;
  }
);
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="grid gap-4 sm:grid-cols-3">
      <SalesPropertyReferenceField
        :filled="isFilled(identity.marital_status)"
        indicator="select"
        :label="maritalStatusLabel"
        :error="errors.marital_status"
        required
        test-id="sales-party-marital-status"
      >
        <select
          :value="identity.marital_status || ''"
          class="appearance-none pr-5"
          :class="
            referenceControlClasses({
              error: errors.marital_status,
              filled: isFilled(identity.marital_status),
            })
          "
          @change="update('marital_status', $event.target.value)"
        >
          <option
            v-for="option in maritalStatuses"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </SalesPropertyReferenceField>
    </div>

    <div v-if="isMarried" class="grid gap-4 sm:grid-cols-3">
      <SalesPropertyReferenceField
        :filled="isFilled(identity.marital_property_regime)"
        indicator="select"
        :label="t('SALES_PARTIES.FORM.MARRIAGE_REGIME')"
        :error="errors.marital_property_regime"
        required
        test-id="sales-party-marriage-regime"
      >
        <select
          :value="identity.marital_property_regime || ''"
          class="appearance-none pr-5"
          :class="
            referenceControlClasses({
              error: errors.marital_property_regime,
              filled: isFilled(identity.marital_property_regime),
            })
          "
          @change="update('marital_property_regime', $event.target.value)"
        >
          <option
            v-for="option in marriageRegimes"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </SalesPropertyReferenceField>
    </div>

    <div v-if="canDeclareStableUnion" class="grid gap-4 sm:grid-cols-3">
      <div class="flex min-h-[82px] items-center">
        <LaisToggle
          :model-value="hasStableUnion"
          :label="t('SALES_PARTIES.FORM.STABLE_UNION')"
          @update:model-value="update('stable_union', $event)"
        />
      </div>
    </div>

    <div v-if="needsSpouse" class="grid gap-4 sm:grid-cols-3">
      <SalesPropertyReferenceField
        v-if="showSpouseCpf"
        :filled="isFilled(spouseCpf)"
        :disabled="spouseLookupLoading"
        :label="t('SALES_PARTIES.FORM.SPOUSE_CPF')"
        :error="spouseLookupError || errors.spouse_cpf"
        :show-success="!spouseLookupLoading"
        test-id="sales-party-spouse-cpf"
      >
        <div class="relative">
          <input
            :value="spouseCpf"
            inputmode="numeric"
            maxlength="14"
            :placeholder="t('SALES_PARTIES.FORM.CPF_PLACEHOLDER')"
            :disabled="spouseLookupLoading"
            :class="
              referenceControlClasses({
                disabled: spouseLookupLoading,
                error: spouseLookupError || errors.spouse_cpf,
                filled: isFilled(spouseCpf),
              })
            "
            @input="updateSpouseCpf($event.target.value)"
          />
          <span
            v-if="spouseLookupLoading"
            aria-hidden="true"
            class="pointer-events-none absolute inset-y-0 right-3 flex items-center text-lais-primary"
            data-testid="sales-party-spouse-lookup-loading"
          >
            <span class="i-lucide-loader-circle size-4 animate-spin" />
          </span>
        </div>
      </SalesPropertyReferenceField>
      <SalesPropertyReferenceField
        :filled="isFilled(identity.spouse_name)"
        :disabled="spouseLookupLoading"
        :label="spouseNameLabel"
        :error="errors.spouse_name"
        required
        :show-success="!spouseLookupLoading"
        test-id="sales-party-spouse-name"
      >
        <input
          :value="identity.spouse_name"
          :placeholder="t('SALES_PARTIES.FORM.NAME_PLACEHOLDER')"
          :disabled="spouseLookupLoading"
          :class="
            referenceControlClasses({
              disabled: spouseLookupLoading,
              error: errors.spouse_name,
              filled: isFilled(identity.spouse_name),
            })
          "
          @input="update('spouse_name', $event.target.value)"
        />
      </SalesPropertyReferenceField>
    </div>
    <SalesPartyMarriageAgreementAlert
      :show="showMarriageAgreement"
      @close="marriageAgreementDismissed = true"
    />
  </div>
</template>
